import {
  collection, addDoc, getDocs, getDoc, updateDoc,
  doc, query, where, orderBy, serverTimestamp, documentId,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from './firebase';

const COLLECTION = 'classes';

export interface ClassDoc {
  id: string;
  name: string;
  description?: string;
  active?: boolean;
  imageUrl?: string;
  desiredRetention?: number;
  createdAt?: unknown;
  [key: string]: unknown;
}

const deleteClassCallable = httpsCallable(functions, 'deleteClass');
const renameClassStatsCallable = httpsCallable(functions, 'syncClassStatsName');

export async function createClass(name: string, description = ''): Promise<string> {
  const trimmed = name.trim();
  const dupeCheck = query(collection(db, COLLECTION), where('name', '==', trimmed));
  const dupeSnap = await getDocs(dupeCheck);
  if (!dupeSnap.empty) {
    throw new Error('يوجد مادة بنفس الاسم بالفعل');
  }

  const docRef = await addDoc(collection(db, COLLECTION), {
    name: trimmed,
    description,
    active: true,
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function getClasses(): Promise<ClassDoc[]> {
  const q = query(collection(db, COLLECTION), orderBy('name', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ClassDoc);
}

export async function getActiveClasses(): Promise<ClassDoc[]> {
  const q = query(
    collection(db, COLLECTION),
    where('active', '!=', false),
    orderBy('active'),
    orderBy('name', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ClassDoc);
}

export async function getClassesByIds(ids: string[]): Promise<ClassDoc[]> {
  if (!ids?.length) return [];
  const BATCH = 30;
  const promises = [];
  for (let i = 0; i < ids.length; i += BATCH) {
    const batch = ids.slice(i, i + BATCH);
    promises.push(getDocs(query(collection(db, COLLECTION), where(documentId(), 'in', batch))));
  }
  const results: ClassDoc[] = [];
  const snaps = await Promise.all(promises);
  snaps.forEach(snap => snap.docs.forEach(d => results.push({ id: d.id, ...d.data() } as ClassDoc)));
  return results.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

export async function getClassDoc(id: string): Promise<ClassDoc | null> {
  const snap = await getDoc(doc(db, COLLECTION, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as ClassDoc;
}

export async function checkClassNameExists(name: string, excludeId: string | null = null): Promise<boolean> {
  const trimmed = name.trim();
  const q = query(collection(db, COLLECTION), where('name', '==', trimmed));
  const snap = await getDocs(q);
  return snap.docs.some(d => d.id !== excludeId);
}

export async function updateClass(id: string, data: Partial<ClassDoc>): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), data);
  if (data.name) {
    renameClassStatsCallable({ classId: id, newName: data.name }).catch(() => {});
  }
}

export async function deleteClass(id: string): Promise<unknown> {
  const result = await deleteClassCallable({ classId: id });
  return result.data;
}

export async function uploadClassImage(classId: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop();
  const storageRef = ref(storage, `classes/${classId}/cover.${ext}`);
  const snap = await uploadBytes(storageRef, file);
  return getDownloadURL(snap.ref);
}

export async function deleteClassImage(url: string): Promise<void> {
  try {
    const storageRef = ref(storage, url);
    await deleteObject(storageRef);
  } catch {
    // already deleted or invalid
  }
}
